/**
 * Keep the focused service field above the OS keyboard and the sticky
 * Pay / Continue dock. scrollIntoView({ block: 'center' }) uses the layout
 * viewport, so iOS/Android keyboards still cover the input.
 */

const FIELD_SELECTOR = 'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea';
const DOCK_SELECTOR = '[data-keyboard-avoid-dock="true"]';
const PAD = 12;

let installed = false;
let lastField: HTMLElement | null = null;
let scrollTimer = 0;

export function isServiceTextField(node: EventTarget | null): node is HTMLElement {
  if (!(node instanceof HTMLElement)) return false;
  return node.matches(FIELD_SELECTOR) && !node.closest('[data-keyboard-avoid-skip]');
}

export function visualViewportBand(): { top: number; bottom: number; height: number } {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const top = vv ? vv.offsetTop : 0;
  const height = vv ? vv.height : (typeof window !== 'undefined' ? window.innerHeight : 0);
  return { top, height, bottom: top + height };
}

export function overlappingBottomChromePx(): number {
  if (typeof document === 'undefined') return 0;
  const { top, bottom } = visualViewportBand();
  let chrome = 0;
  document.querySelectorAll(DOCK_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.getAttribute('data-service-request-footer-bare') === 'true') return;
    const r = node.getBoundingClientRect();
    if (r.height < 8) return;
    const overlap = Math.min(r.bottom, bottom) - Math.max(r.top, top);
    if (overlap > 0 && r.bottom >= bottom - 24) {
      chrome = Math.max(chrome, Math.min(r.height, overlap));
    }
  });
  return chrome;
}

function nearestScrollParent(el: HTMLElement): HTMLElement | null {
  let n: HTMLElement | null = el.parentElement;
  while (n && n !== document.body && n !== document.documentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && n.scrollHeight > n.clientHeight + 2) {
      return n;
    }
    n = n.parentElement;
  }
  return null;
}

function applyDelta(el: HTMLElement, delta: number): void {
  if (!delta) return;
  const parent = nearestScrollParent(el);
  if (parent) {
    parent.scrollTop += delta;
    return;
  }
  window.scrollBy(0, delta);
}

/** Scroll `el` into the visual viewport, leaving room for the sticky dock. */
export function scrollFieldIntoVisibleViewport(el: HTMLElement): boolean {
  if (typeof window === 'undefined' || !el.isConnected) return false;
  const { top: viewTop, bottom: viewBottom } = visualViewportBand();
  const chrome = overlappingBottomChromePx();
  const scroller = nearestScrollParent(el);
  const scrollRect = scroller?.getBoundingClientRect();
  // Dialog headers and actions are outside this scrollport, even when they
  // don't overlap the visual viewport's bottom edge.
  const clipped = scrollRect && scrollRect.height > 0 ? scrollRect : null;
  const usableTop = Math.max(viewTop, clipped?.top ?? viewTop) + PAD;
  const usableBottom = Math.min(viewBottom - chrome, clipped?.bottom ?? viewBottom) - PAD;
  if (usableBottom - usableTop < 48) return false;
  const rect = el.getBoundingClientRect();
  if (rect.height <= 0) return false;
  let delta = 0;
  if (rect.height > usableBottom - usableTop) delta = rect.top - usableTop;
  else if (rect.bottom > usableBottom) delta = rect.bottom - usableBottom;
  else if (rect.top < usableTop) delta = rect.top - usableTop;
  if (!delta) return false;
  applyDelta(el, delta);
  return true;
}

function scheduleScroll(el: HTMLElement): void {
  const run = () => {
    if (!el.isConnected) return;
    scrollFieldIntoVisibleViewport(el);
  };
  run();
  requestAnimationFrame(run);
  window.clearTimeout(scrollTimer);
  scrollTimer = window.setTimeout(run, 180);
  window.setTimeout(run, 360);
}

/** App-wide: focus + keyboard show/hide keep the field in the visible band. */
export function installServiceFieldKeyboardAvoidance(): void {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  const onFocusIn = (event: FocusEvent) => {
    const t = event.target;
    if (!isServiceTextField(t)) return;
    lastField = t;
    scheduleScroll(t);
  };

  const onFocusOut = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      lastField = isServiceTextField(active) ? active : null;
    }, 0);
  };

  const onViewport = () => {
    if (!lastField || !lastField.isConnected) return;
    if (document.activeElement !== lastField) return;
    scheduleScroll(lastField);
  };

  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);
  window.visualViewport?.addEventListener('resize', onViewport);
  window.visualViewport?.addEventListener('scroll', onViewport);
}
